import { useEffect, useRef, useState } from 'react';
import { authFetch } from '../utils/auth-fetch';

export interface SubBatchPhase {
  startedAt: string | null;
  succeeded?: number;
  failed?: number;
  skipped?: number;
  generated?: number;
  validated?: number;
  provider?: string | null;
  batchId?: string | null;
}

export interface SubBatch {
  id: string;
  runId: string;
  batchNumber: number;
  currentPhase: string;
  status: string;
  productCount: number;
  enrichment: SubBatchPhase;
  benefitScoring: SubBatchPhase;
  embedding: SubBatchPhase;
  aeoGeneration: SubBatchPhase;
  aeoValidation: SubBatchPhase;
  siteDomain: string;
  triggerType: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface SiteRow {
  siteId: string;
  domain: string;
  configId: string;
  configName: string;
  scheduleFrequency: string;
  nextRunAt: string | null;
  configStatus: string;
  latestRunId: string | null;
  latestRunStatus: string | null;
  latestRunStartedAt: string | null;
  triggerType: string | null;
  activeRunCount: number;
}

export interface TranslationJob {
  id: string;
  siteId: string;
  siteDomain: string;
  targetLanguage: string;
  sourceType: string;
  provider: string;
  model: string;
  status: string;
  totalItems: number;
  completedItems: number;
  failedItems: number;
  submittedAt: string | null;
  createdAt: string | null;
}

export interface AsyncJob {
  id: string;
  jobType: string;
  status: string;
  resourceId: string | null;
  progressPercent: number | null;
  progressMessage: string | null;
  siteDomain: string | null;
  startedAt: string | null;
  createdAt: string | null;
}

export interface WaveStrip {
  entry: number;
  enrich: number;
  scoring: number;
  embed: number;
  aeoGen: number;
  validate: number;
  trans: number;
  done: number;
  doneLabel: string;
}

export interface QueueDepths {
  asyncJobsPending: number;
  asyncJobsProcessing: number;
  subBatchesPending: number;
  subBatchesProcessing: number;
  translationPending: number;
}

export interface Alert {
  name: string;
  severity: 'critical' | 'warning';
  firing: boolean;
  message: string;
  [extra: string]: unknown;
}

export interface PipelineSnapshot {
  cachedAt: string;
  cacheTtlMs: number;
  sites: SiteRow[];
  subBatches: SubBatch[];
  realtimeBatches: SubBatch[];
  translationJobs: TranslationJob[];
  asyncJobs: AsyncJob[];
  waveStrip: WaveStrip;
  queues: QueueDepths;
  workerConfig: { parallelSubBatches: number; maxAeoTransitions: number };
  workerActive: { subBatchesProcessing: number; aeoInFlight: number };
  alerts: Alert[];
}

export function usePipelineSnapshot(intervalMs = 5000) {
  const [snapshot, setSnapshot] = useState<PipelineSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef<PipelineSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await authFetch('/api/pipeline-monitor/snapshot');
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as PipelineSnapshot;
        if (cancelled) return;
        prevRef.current = snapshot; // capture previous before overwrite
        setSnapshot(data);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOnce();
    const id = setInterval(() => {
      // skip polling when tab is hidden to save bandwidth
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchOnce();
    }, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return { snapshot, prevSnapshot: prevRef.current, error, loading };
}
